<!-- @format -->
<template>
    <div class="marketplace-disclaimer-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Review Partner Application Disclaimer</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading" class="loading-screen">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="disclaimer-text-box">
                        <h3>Disclaimer</h3>
                        <p>
                            This is the disclaimer the applicant will see upon requesting
                            partnership with you. Feel free to make edits to it and add your own
                            personality.
                        </p>
                    </div>
                    <div class="editor">
                        <app-text-editor
                            ref="textEditor"
                            v-model="body"
                            :config="froalaConfig"
                            :userId="null"
                            :showBrandingColors="false"
                            :injectOptions="['Link', 'Inline']"
                            @insertSignature="insertSignature($event)"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button
                        class="cancel-btn transparent-btn"
                        size="normal"
                        @click="closeWidget"
                    >
                        <span class="accordian-arrow fa fa-chevron-left back-logo"></span>
                        Back
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        :disabled="!hasChanged"
                        class="transparent-btn"
                        size="normal"
                        @click="openRevertToDefaultModal"
                        >Revert to Default</app-button
                    >
                    <app-button class="next-btn" @click="submitDisclaimer"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppTextEditor = () => import('@/components/common/app-text-editor');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { StorefrontService } from '@/scripts/services/storefront/StorefrontService';
import AppPopup from '@/components/popups/app-popup';

export default {
    name: 'marketplace-disclaimer-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-text-editor': AppTextEditor,
        'loading-screen-svg': LoadingScreenSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            agencyName: null,

            isLoading: false,
            initialBody: this.widget.data.disclaimer,
            body: null,
            froalaConfig: {
                key: this.$helpers.getFroalaKey(),
                toolbarButtons: [
                    'fontSize',
                    'emoticons',
                    'formatUL',
                    'bold',
                    'italic',
                    'colorPicker',
                    'insertLink',
                    'undo',
                    'redo',
                ],

                useClasses: false,
                fontFamilySelection: true,
                fontSizeSelection: true,
                paragraphFormatSelection: true,
                fontFamily: this.$helpers.minimalFontStyles,
                emoticonsUseImage: false,

                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],

                requestHeaders: {
                    Authorization: `Bearer ${localStorage.jwt}`,
                },
            },
        };
    },

    computed: {
        defaultBody() {
            return `By connecting to ${this.agencyName} you agree to give them access to view your instance of GlassHive. All data viewed is strictly used to improve our partners' success and provide us with information on how to improve content and process around marketing and sales. 
            <br>
            As a Preferred partner you must begin the Partner program campaign and within 60 days of partnership approval. You must complete the marketing plan as created or partnership can be canceled by Sponsor.
            <br>
            [Partner Name] acknowledges that the Marketing Plan of ${this.agencyName} may not be reproduced in any form, including but not limited to, transcribe, distribute, or redistribute for any purpose outside of the confinements of GlassHive.`;
        },

        hasChanged() {
            if (this.body !== this.initialBody) {
                return true;
            }
            return false;
        },
    },

    created() {
        this.getAgencyName();
        if (this.initialBody) {
            this.body = this.initialBody;
        } else {
            this.body = this.defaultBody;
        }
    },

    methods: {
        getAgencyName() {
            var filterParam = `$filter=Id eq ${this.widget.data.agencyId}`;
            var selectParam = `$select=Id,Name`;

            var query = `/service/Agencies?${filterParam}&${selectParam}`;

            this.$api.get(query).then((response) => {
                this.agencyName = response.data.value[0].Name;
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        submitDisclaimer() {
            this.editStorefront();
        },

        openRevertToDefaultModal() {
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: `Attention?`,
                    content: `<p>Are you sure you want to undo any changes you might have made? The disclaimer will change back to its default state.</p>`,
                    actions: {
                        confirm: {
                            label: `Revert to Default`,
                            action: this.revertToDefault,
                        },
                        cancel: {
                            label: 'Cancel',
                        },
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },

        revertToDefault() {
            this.body = this.defaultBody;
        },

        editStorefront() {
            this.isLoading = true;
            var request = {
                Disclaimer: this.body,
            };

            StorefrontService.patch(this.widget.data.agencyId, request)
                .then(() => {
                    this.$helpers.showSuccessNotification('Disclaimer successfully saved!');
                    EventBus.$emit('update-disclaimer');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your Storefront',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.marketplace-disclaimer-widget.widget .widget-header {
    background: rgb(80, 82, 254);
    background: linear-gradient(90deg, rgba(80, 82, 254, 1) 0%, rgba(75, 201, 254, 1) 100%);
}

.marketplace-disclaimer-widget .button-link,
.marketplace-disclaimer-widget .action-container {
    width: 100%;
}

.marketplace-disclaimer-widget .content-wrapper {
    padding: 0 !important;
}

.marketplace-disclaimer-widget .disclaimer-text-box {
    padding: 24px;
}

.marketplace-disclaimer-widget .app-text-editor {
    border: 1px solid #cac6cb;
    border-radius: 11px;
}

.marketplace-disclaimer-widget .editor {
    padding: 0px 24px;
}

.marketplace-disclaimer-widget .transparent-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.marketplace-disclaimer-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.marketplace-disclaimer-widget .cancel-btn {
    background: transparent;
    color: #382153;
    border: 2px solid #cac6cb;
}

.marketplace-disclaimer-widget .action-container {
    display: flex;
    justify-content: flex-end;
    column-gap: 13px;
}

.marketplace-disclaimer-widget h3 {
    font-size: 18px;
}

.marketplace-disclaimer-widget p {
    font-size: 16px;
}

.marketplace-disclaimer-widget .back-logo {
    padding-right: 12px;
}
.marketplace-disclaimer-widget .loading-screen {
    text-align: center;
}
</style>
